import { Backdrop, Divider, List, useTheme } from "@mui/material";
import { useUserContext } from "../../contexts/user-context/UserContext";
import signalRHubConfiguration, { SignalRHubEnum } from "../../hooks/use-signalr/SignalRHubInfo";
import { useSignalR } from "../../hooks/use-signalr/UseSignalR";
import { useNotificationMessages } from "../../queries/notification-messages/UseNotificationMessagess";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { INotificationMessageModel, NotificationMessageTypeEnum } from "../../services/notification-message-service/Models";
import settings from "../../app-settings.json";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useSaveNotificationMessage } from "../../queries/notification-messages/UseSaveNotificationMessage";
import { UserMessageActionEnum, UserMessageListItem } from "../user-messages/UserMessageListItem";
import React from "react";
import ResponsiveDialog, { ResponsiveDialogCloseReason } from "../responsive-dialog/ResponsiveDialog";

export default function LoginMessagesDialog() {
    const [userState] = useUserContext();
    const theme = useTheme();
    const routeContext = useRouteContext();

    const { data: messages, refetch } = useQueryNotifications(useNotificationMessages)({
        groupID: userState.startGroupID as number,
        enabled: (userState.startGroupID ?? 0) > 0
    });

    // We need to subscribe using CustomerID -1 here because login messages
    // that are created from SupportCenter with the notify logged in users
    // option checked will be published as AdHoc messages that have a
    // CustomerID of -1.
    // Also, we only render this dialog when not offline
    const hubConfiguration = signalRHubConfiguration.find(o => o.id === SignalRHubEnum.UserMessage);
    useSignalR({
        url: `${settings.apiBaseUrl}${hubConfiguration?.path}`,
        customerID: -1,
        methodName: hubConfiguration?.methodName as string,
        onCallback: () => { refetch() },
        offline: false
    });

    const { mutate: acknowledgeMessage } = useQueryNotifications(useSaveNotificationMessage)({
        groupID: routeContext.groupID as number
    })

    const unreadUserMessages = messages
        ?.filter(o => o.messageType === NotificationMessageTypeEnum.UserMessage
            && o.acknowledged === null)
        ?? [];

    const hasUnreadUserMessages = unreadUserMessages.length > 0;

    async function onAction(action: UserMessageActionEnum, payload: INotificationMessageModel) {
        switch (action) {
            case UserMessageActionEnum.Acknowledge: {
                if (payload.messageType === NotificationMessageTypeEnum.UserMessage) {
                    // We'll put a temp value in for acknowledged so that the mutation
                    // that updates the in-memory query data will have a non-null value
                    // in the field so that it won't show up as being unread. When the
                    // refetch fires, this value will be updated with the actual value.
                    // We don't use this value for anything other than determining whether
                    // or not the message is unread, and this temp value should be a very
                    // close estimate of the final value, so this should not cause any
                    // problems.
                    payload.acknowledged = new Date();
                    acknowledgeMessage(payload);
                }

                break;
            }
        }
    }

    return (
        <Backdrop
            sx={{
                zIndex: theme.zIndex.drawer + 1,
                color: '#fff'
            }}
            open={hasUnreadUserMessages}>
            <ResponsiveDialog
                hideCancel
                hideOkay
                open={hasUnreadUserMessages}
                title={""}
                onClose={function (reason: ResponsiveDialogCloseReason): void {
                    // we don't need to do anything on close. This dialog is only
                    // closed by acknowledging all of the contained user messages.
                } }
            >
                <List sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto',
                    bgcolor: 'background.paper'
                }}>
                    {unreadUserMessages.map((o, index) => (
                        <React.Fragment key={index}>
                            <UserMessageListItem
                                item={o}
                                onAction={onAction}
                                expanded={false}
                                />
                            {
                            index < unreadUserMessages.length - 1 &&
                            <Divider variant="inset" component="li" />
                            }
                        </React.Fragment>
                    ))
                    }
                </List>
            </ResponsiveDialog>
        </Backdrop>

    );
}